import { Box, IconButton, MenuItem, StandardTextFieldProps, Typography } from "@mui/material";
import { ChevronDownIcon } from "src/foundations/icons";
import { BaseDropdownOption, BaseDropdownProps } from "src/components/inputs/dropdown/commonTypes";
import { ChangeEvent } from "react";
import { SenecTextField } from "src/components/inputs/input-field/SenecTextField";
import { shadowMedium } from "src/styles/components/constants";

export type StandardDropdownProps = BaseDropdownProps & {
    /**
     * Array of options.
     */
    options: BaseDropdownOption[] | string[];
    /**
     * Variant of textfield "outlined" or "filled"
     */
    variant?: "outlined" | "filled";
    /**
     * Size of textfield "small" or "medium"
     */
    size?: "small" | "medium";
} & Omit<StandardTextFieldProps, "onChange" | "label" | "variant">;

/**
 * Component for a custom drop down selection field.
 * - Based on MUI TextField.
 */
export const StandardDropdown = (props: StandardDropdownProps) => {
    const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (props.onChange) {
            props.onChange(e.target.value);
        }
    };

    return (
        <SenecTextField
            {...props}
            sx={{
                ...props.sx,
                ".MuiSelect-select.MuiInputBase-inputSizeSmall": {
                    transform: "translateY(1px) !important",
                },
            }}
            variant={props.variant}
            onChange={(e) => handleOnChange(e)}
            data-testid="standard-dropdown"
            select
            value={props.value}
            label={props.label}
            SelectProps={{
                MenuProps: {
                    sx: {
                        ".MuiPaper-root": {
                            boxShadow: shadowMedium,
                            borderRadius: "0.75rem",
                            padding: ".5rem",
                        },
                    },
                },
                IconComponent: (iconProps, context) => (
                    <Box sx={{ position: "relative" }}>
                        <IconButton
                            disableRipple={true}
                            sx={{
                                m: "0",
                                w: 0,
                                "&:hover, &:active, &:focus": {
                                    cursor: "none",
                                    backgroundColor: "transparent",
                                },
                                position: "absolute",
                                right: "10px",
                                bottom: props.size === "small" ? "-8px" : "-10px",
                            }}
                        >
                            <ChevronDownIcon
                                {...iconProps}
                                {...context}
                                sx={{
                                    "&:hover": { cursor: "pointer", backgroundColor: "gray" },
                                    w: "100%",
                                }}
                            />
                        </IconButton>
                    </Box>
                ),
            }}
        >
            {props.options.map((option, index) => (
                <MenuItem value={typeof option === "string" ? option : option.value} key={index}>
                    <Typography
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", marginRight: "12px", width: "100%", pt: "3px" }}
                        variant={props.size === "small" ? "enbwTabletS1" : "enbwTabletP1"}
                    >
                        {typeof option === "string" ? option : option.label || option.value}
                    </Typography>
                </MenuItem>
            ))}
        </SenecTextField>
    );
};
